export interface EventLocationAddress {
  place?: string | null;
  coordinates: number[];
  city: {
    coordinates: number[];
    name?: string | null;
  };
  country?: {
    name?: string | null;
  } | null;
}

export type EventLocationSource = EventLocationAddress | {
  addresses?: EventLocationAddress[] | null;
} | null | undefined;

export interface EventCoordinates {
  lat: number;
  lon: number;
}

function getAddress(source: EventLocationSource): EventLocationAddress | undefined {
  if (!source) {
    return undefined;
  }

  if ('addresses' in source) {
    return (source as {addresses?: EventLocationAddress[] | null}).addresses?.[0];
  }

  return source as EventLocationAddress;
}

/**
 * API and PostGIS expose points in GeoJSON order: longitude, latitude.
 */
export function getEventCoordinates(source: EventLocationSource): EventCoordinates | null {
  const address = getAddress(source);
  const coordinates = address?.coordinates?.length === 2
    ? address.coordinates
    : address?.city?.coordinates;
  const lon = Number(coordinates?.[0]);
  const lat = Number(coordinates?.[1]);

  return Number.isFinite(lat) && Number.isFinite(lon) && lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180
    ? {lat, lon}
    : null;
}

/**
 * Builds the canonical event-location label from either an address or an event.
 */
export function formatEventLocation(source: EventLocationSource): string {
  const address = getAddress(source);
  const seen = new Set<string>();

  return [address?.place, address?.city?.name, address?.country?.name]
    .reduce<string[]>((parts, value) => {
      const segment = value?.trim();
      const key = segment?.toLocaleLowerCase();

      if (!segment || !key || seen.has(key)) {
        return parts;
      }

      seen.add(key);
      parts.push(segment);
      return parts;
    }, [])
    .join(', ');
}
